import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const SESSION_TOKEN_KEY = "app_session_token";
const CHECK_INTERVAL = 30_000; // 30s

function getLocalToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

function setLocalToken(token: string) {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
}

export function clearLocalToken() {
  localStorage.removeItem(SESSION_TOKEN_KEY);
}

/** Generate a random session token */
function generateToken(): string {
  return crypto.randomUUID();
}

/**
 * Register a new session in the DB. Returns whether another session existed.
 * If `force` is true, overwrites any existing session.
 */
export async function registerSession(
  userId: string,
  force = false
): Promise<{ hadExisting: boolean; registered: boolean }> {
  // Check existing session
  const { data: existing } = await supabase
    .from("user_active_sessions")
    .select("session_token")
    .eq("user_id", userId)
    .maybeSingle();

  const localToken = getLocalToken();
  const hadExisting = !!existing && existing.session_token !== localToken;

  if (hadExisting && !force) {
    return { hadExisting: true, registered: false };
  }

  const newToken = generateToken();

  if (existing) {
    await supabase
      .from("user_active_sessions")
      .update({
        session_token: newToken,
        device_info: navigator.userAgent.substring(0, 200),
        last_seen_at: new Date().toISOString(),
      })
      .eq("user_id", userId);
  } else {
    await supabase.from("user_active_sessions").insert({
      user_id: userId,
      session_token: newToken,
      device_info: navigator.userAgent.substring(0, 200),
    });
  }

  setLocalToken(newToken);
  return { hadExisting, registered: true };
}

/** Remove session from DB on logout */
export async function clearSession(userId: string) {
  clearLocalToken();
  await supabase
    .from("user_active_sessions")
    .delete()
    .eq("user_id", userId);
}

/**
 * Hook that periodically checks if this session is still the active one.
 * If another device took over, forces sign out.
 */
export function useSessionGuard() {
  const navigate = useNavigate();
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const checkSession = useCallback(async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return;

    const localToken = getLocalToken();
    if (!localToken) return;

    const { data } = await supabase
      .from("user_active_sessions")
      .select("session_token")
      .eq("user_id", session.user.id)
      .maybeSingle();

    if (data && data.session_token !== localToken) {
      // Another device took over
      clearLocalToken();
      await supabase.auth.signOut();
      navigate("/", { replace: true });
      // Show message after redirect
      setTimeout(() => {
        import("sonner").then(({ toast }) => {
          toast.error("Sessão encerrada", {
            description:
              "Sua conta foi acessada em outro dispositivo. Faça login novamente.",
            duration: 8000,
          });
        });
      }, 500);
    }
  }, [navigate]);

  useEffect(() => {
    // Initial check after mount
    const timeout = setTimeout(checkSession, 3000);
    // Periodic checks
    intervalRef.current = setInterval(checkSession, CHECK_INTERVAL);

    return () => {
      clearTimeout(timeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [checkSession]);
}
